import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';
import pt from './locales/pt.json';

// Language detection configuration
const detectionOptions = {
  // Detection order: check URL params first, then browser settings, then localStorage, then cookies
  order: ['querystring', 'navigator', 'localStorage', 'cookie', 'htmlTag'],
  
  // Look for language in query string parameter 'lang' or 'lng'
  lookupQuerystring: 'lang',
  
  // Look for language in cookie
  lookupCookie: 'i18next',
  
  // Look for language in localStorage
  lookupLocalStorage: 'i18nextLng',
  
  // Cache user language preference
  caches: ['localStorage', 'cookie'],
  
  // Cookie options
  cookieMinutes: 10080, // 7 days
  cookieDomain: window.location.hostname,
  
  // Exclude certain paths from detection (useful for admin paths)
  excludeCacheFor: ['cimode'],
  
  // Convert browser language codes to our supported languages
  // This handles regional variants like pt-BR, pt-PT, en-US, en-GB
  convertDetectedLanguage: (lng: string) => {
    // Extract base language code (e.g., 'pt' from 'pt-BR')
    const baseLanguage = lng.split('-')[0].toLowerCase();
    
    // Map to supported languages
    const supportedLanguages = ['en', 'pt'];
    
    if (supportedLanguages.includes(baseLanguage)) {
      if (import.meta.env.DEV) {
        console.log(`[i18n] Detected language: ${lng} → ${baseLanguage}`);
      }
      return baseLanguage;
    }
    
    // Default to English if language not supported
    if (import.meta.env.DEV) {
      console.log(`[i18n] Unsupported language detected: ${lng}, falling back to English`);
    }
    return 'en';
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      pt: { translation: pt },
    },
    
    // Supported languages
    supportedLngs: ['en', 'pt'],
    
    // Fallback language if detected language is not available
    fallbackLng: 'en',
    
    // Enable debug mode in development to see detection process
    debug: import.meta.env.DEV,
    
    // Allow keys to be phrases having `:`, `.`
    keySeparator: '.',
    
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    // Language detection options
    detection: detectionOptions,
    
    // React options
    react: {
      useSuspense: false, // Disable suspense to avoid loading issues
    },
  });

// Log initial language detection in development
if (import.meta.env.DEV) {
  console.log(`[i18n] Language initialized: ${i18n.language}`);
  console.log(`[i18n] Browser language: ${navigator.language}`);
  console.log(`[i18n] Stored preference: ${localStorage.getItem('i18nextLng')}`);
}

export default i18n;
